import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Box,
  useToast,
  VStack,
  HStack,
  Image,
  Text,
} from "components-lib";
import FileUploadField from "../../../components/FileUploadField";
import { axiosInstance } from "config/api";
import { humanizeApiError } from "../../../utils/api_error_samples";

const defaultForm = {
  name: "",
  email: "",
  phone: "",
  whatsapp: "",
  address: "",
  // image URLs (existing)
  clinic_icon: "",
  qr_code: "",
  bar_code: "",
  // selected File objects before upload
  clinic_icon_file: null,
  qr_code_file: null,
  bar_code_file: null,
  payment: {
    upi: "",
    bank: {
      account_holder: "",
      account_number: "",
      bank_name: "",
      ifsc_code: "",
    },
  },
};

const createEmptyForm = () => ({
  ...defaultForm,
  payment: {
    ...defaultForm.payment,
    bank: {
      ...defaultForm.payment.bank,
    },
  },
});

const parseBankDetails = (bankDetails) => {
  const initial = {
    account_holder: "",
    account_number: "",
    bank_name: "",
    ifsc_code: "",
  };

  if (!bankDetails || typeof bankDetails !== "string") {
    return initial;
  }

  const segments = bankDetails.split(",");
  segments.forEach((segment) => {
    const section = segment.trim();
    const lower = section.toLowerCase();

    if (lower.startsWith("account no") || lower.startsWith("a/c")) {
      const value = section.split(":").slice(1).join(":").trim();
      initial.account_number = value || initial.account_number;
      return;
    }

    if (lower.startsWith("ifsc")) {
      const value = section.split(":").slice(1).join(":").trim();
      initial.ifsc_code = value || initial.ifsc_code;
      return;
    }

    if (lower.startsWith("holder")) {
      const value = section.split(":").slice(1).join(":").trim();
      initial.account_holder = value || initial.account_holder;
      return;
    }

    if (!initial.bank_name) {
      initial.bank_name = section;
    }
  });

  return initial;
};

const mapClinicToForm = (clinic) => {
  if (!clinic) return createEmptyForm();

  // Support both camelCase and snake_case API shapes
  return {
    name: clinic.clinicName || clinic.clinic_name || "",
    email: clinic.clinicEmail || clinic.clinic_email || clinic.clinicEmail || clinic.clinic_email || "",
    phone: clinic.phone || clinic.phoneno || "",
    whatsapp: clinic.whatsapp || clinic.whatsapp_no || "",
    address: clinic.address || "",
    clinic_icon: clinic.clinic_icon || clinic.clinicIcon || "",
    qr_code: clinic.qr_code || clinic.qrCode || "",
    bar_code: clinic.bar_code || clinic.barCode || "",
    payment: {
      upi: clinic.upiDetails || clinic.upi_details || "",
      bank: parseBankDetails(clinic.bankDetails || clinic.bank_details),
    },
  };
};

const buildBankDetailsString = (bank) => {
  if (!bank || typeof bank !== "object") return "";
  const parts = [];

  if (bank.bank_name) {
    parts.push(bank.bank_name.trim());
  }

  if (bank.account_holder) {
    parts.push(`Holder: ${bank.account_holder.trim()}`);
  }

  if (bank.account_number) {
    parts.push(`Account No: ${bank.account_number.trim()}`);
  }

  if (bank.ifsc_code) {
    parts.push(`IFSC: ${bank.ifsc_code.trim()}`);
  }

  return parts.join(", ");
};

const ClinicEditModal = ({ isOpen, onClose, clinic, onClinicUpdated }) => {
  const [form, setForm] = useState(createEmptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const toast = useToast();
  const [updatereponse,Setupdatereponse] = useState(null);
  useEffect(() => {
    let mounted = true;

    const fetchClinicById = async (id) => {
      if (!id) return;
      setIsFetching(true);
      try {
        // endpoint used in example: /api2/api/clinic/clinicById?id=23
        const response = await axiosInstance.request({
          method: "get",
          url: `api/clinic/clinicById`,
          params: { id },
          validateStatus: (s) => s < 500,
        });

        // Support various response shapes:
        // - response.data.clinic
        // - response.data (clinic object directly)
        // - response.data.data
        const clinicData =
          response.data?.clinic ??
          response.data ??
          response.data?.data ??
          null;

        if (mounted) {
          setForm(mapClinicToForm(clinicData));
        }
      } catch (err) {
        if (mounted) {
          toast({
            title: "Failed to load clinic",
            description:
              err?.response?.data?.message || "Unable to fetch clinic details.",
            status: "error",
            duration: 4000,
            isClosable: true,
            position: "top",
          });
        }
      } finally {
        if (mounted) setIsFetching(false);
      }
    };

    if (!isOpen) {
      if (mounted) setForm(createEmptyForm());
    } else if (clinic) {
      fetchClinicById(clinic.id);
    } else if (clinic && (clinic.id || clinic._id || clinic.clinic_name || clinic.clinicName)) {
      // if object already provided, use it directly
      setForm(mapClinicToForm(clinic));
    }

    return () => {
      mounted = false;
    };
  }, [clinic, isOpen, toast]);

  const clinicId = useMemo(() => {
    if (!clinic) return "";
    if (typeof clinic === "string" || typeof clinic === "number") return String(clinic);
    return clinic.id || clinic._id || "";
  }, [clinic]);

  const handleInput = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // File upload helper and handlers
  const uploadFile = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axiosInstance.post("/api/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        maxBodyLength: Infinity,
      });
      return res?.data?.objectUrl || res?.data?.url || null;
    } catch (err) {
      console.error("uploadFile error", err);
      throw err;
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({
      ...prev,
      clinic_icon_file: file,
      clinic_icon: file ? URL.createObjectURL(file) : prev.clinic_icon,
    }));
  };

  const handleBarCodeChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({
      ...prev,
      bar_code_file: file,
      bar_code: file ? URL.createObjectURL(file) : prev.bar_code,
    }));
  };

  const handleQrCodeChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({
      ...prev,
      qr_code_file: file,
      qr_code: file ? URL.createObjectURL(file) : prev.qr_code,
    }));
  };

  const handlePaymentChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      payment: {
        ...prev.payment,
        [name]: value,
      },
    }));
  };

  const handleBankChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      payment: {
        ...prev.payment,
        bank: {
          ...prev.payment.bank,
          [name]: value,
        },
      },
    }));
  };

  const submitUpdate = async (payload) => {
    if (!clinicId) throw new Error("Missing clinic id");

    try {
      const response = await axiosInstance.request({
        method: "put",
        url: `/api/clinic/${clinicId}`,
        data: payload,
        validateStatus: (status) => status < 500,
      });
        Setupdatereponse(response);
      if (response.status >= 200 && response.status < 300) return response;
      throw new Error(`Unexpected status ${response.status}`);
    } catch (err) {
      throw err;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!clinicId) return;

    setIsSubmitting(true);
    try {
      const payload = {
        id: clinicId,
        clinic_name: form.name.trim(),
        clinic_email: form.email.trim(),
        phoneno: form.phone.trim(),
        whatsapp_no: form.whatsapp.trim(),
        address: form.address.trim(),
        upi_details: form.payment.upi.trim(),
        bank_details: buildBankDetailsString(form.payment.bank),
      };

      await submitUpdate(payload);

        // Upload any selected files and send their URLs to the clinic upload endpoint
        const fileUrls = {};
        try {
          if (form.clinic_icon_file) {
            fileUrls.clinic_icon = await uploadFile(form.clinic_icon_file);
          }
          if (form.bar_code_file) {
            fileUrls.bar_code = await uploadFile(form.bar_code_file);
          }
          if (form.qr_code_file) {
            fileUrls.qr_code = await uploadFile(form.qr_code_file);
          }

          if (Object.keys(fileUrls).length > 0) {
            const payloadFiles = { id: clinicId, ...fileUrls };
            await axiosInstance.post("/api/clinic/uploadFiles", payloadFiles, {
              headers: { "Content-Type": "application/json" },
            });
          }
        } catch (fileErr) {
          console.error("File upload/update failed:", fileErr);
          toast({
            title: "Updated, but file upload failed",
            description: "Clinic details were saved but uploading images failed.",
            status: "warning",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }

      toast({
        title: "Clinic updated",
        description: "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      if (typeof onClinicUpdated === "function") {
        onClinicUpdated();
      }

      onClose();
    } catch (error) {
      console.error("Clinic update error:", error);
      const serverMessage =  updatereponse?.data?.error || error.response?.data?.error || error.response?.data?.message || error.message;
      const { message: humanMessage, field } = humanizeApiError('/api/clinic', serverMessage);

      // map backend field keys to local form keys
      // if (field === 'clinic_name') {
      //   setForm(prev => ({ ...prev, name: humanMessage }));
      // } else if (field === 'email') {
      //   setForm(prev => ({ ...prev, email: humanMessage }));
      // } else if (field === 'phoneno') {
      //   setForm(prev => ({ ...prev, phone: humanMessage }));
      // }

      toast({
        title: "Update failed",
        description: serverMessage|| humanMessage || error.response?.data?.message || "We could not save the clinic details. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="640px">
        <ModalHeader>Edit Clinic</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Flex gap={3} direction={{ base: "column", md: "row" }}>
                <Box flex="1">
                  <FormControl isRequired>
                    <FormLabel>Clinic Name</FormLabel>
                    <Input name="name" value={form.name} onChange={handleInput} />
                  </FormControl>
                </Box>
                <Box flex="1">
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input name="email" value={form.email} onChange={handleInput} type="email" />
                  </FormControl>
                </Box>
              </Flex>

              <Flex gap={3} direction={{ base: "column", md: "row" }}>
                <Box flex="1">
                  <FormControl isRequired>
                    <FormLabel>Phone</FormLabel>
                    <Input name="phone" onChange={handleInput} value={form.phone} />
                  </FormControl>
                </Box>
                <Box flex="1">
                  <FormControl>
                    <FormLabel>WhatsApp Number</FormLabel>
                    <Input name="whatsapp" value={form.whatsapp} onChange={handleInput} />
                  </FormControl>
                </Box>
              </Flex>

              <FormControl isRequired>
                <FormLabel>Address</FormLabel>
                <Input name="address" value={form.address} onChange={handleInput} />
              </FormControl>

              {/* <Divider /> */}


              <Box>
                <FormLabel>Payment Details</FormLabel>
                <Flex gap={3} direction={{ base: "column", md: "row" }}>
                  <FormControl>
                    <FormLabel>UPI ID</FormLabel>
                    <Input
                      name="upi"
                      value={form.payment.upi}
                      onChange={handlePaymentChange}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Bank Name</FormLabel>
                    <Input
                      name="bank_name"
                      value={form.payment.bank.bank_name}
                      onChange={handleBankChange}
                    />
                  </FormControl>
                </Flex>
                <Flex gap={3} direction={{ base: "column", md: "row" }} mt={3}>
                  <FormControl>
                    <FormLabel>Account Holder</FormLabel>
                    <Input
                      name="account_holder"
                      value={form.payment.bank.account_holder}
                      onChange={handleBankChange}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Account Number</FormLabel>
                    <Input
                      name="account_number"
                      value={form.payment.bank.account_number}
                      onChange={handleBankChange}
                    />
                  </FormControl>
                </Flex>
                <FormControl mt={3}>
                  <FormLabel>IFSC Code</FormLabel>
                  <Input
                    name="ifsc_code"
                    value={form.payment.bank.ifsc_code}
                    onChange={handleBankChange}
                  />
                </FormControl>
              </Box>

              <Box mt={4}>
                <FormLabel>Clinic Images</FormLabel>
                <VStack align="stretch" spacing={3}>
                  <Flex gap={3} direction={{ base: "column", md: "row" }}>
                    <Box flex="1">
                      <FormControl>
                        <FormLabel>Clinic Icon</FormLabel>
                        {form.clinic_icon ? (
                          <Image src={form.clinic_icon} alt="Clinic icon" boxSize="80px" objectFit="cover" mb={2} />
                        ) : (
                          <Text fontSize="sm" color="gray.500" mb={2}>No icon</Text>
                        )}
                        {/* <Input type="file" accept="image/*" onChange={handleLogoChange}
                        /> */}
                      </FormControl>
                    </Box>

                    <Box flex="1">
                      <FormControl>
                        <FormLabel>QR Code</FormLabel>
                        {form.qr_code ? (
                          <Image src={form.qr_code} alt="QR code" boxSize="80px" objectFit="contain" mb={2} />
                        ) : (
                          <Text fontSize="sm" color="gray.500" mb={2}>No QR code</Text>
                        )}
                        {/* <Input type="file" accept="image/*" onChange={handleQrCodeChange} /> */}
                      </FormControl>
                    </Box>

                    <Box flex="1">
                      <FormControl>
                        <FormLabel>Bar Code</FormLabel>
                        {form.bar_code ? (
                          <Image src={form.bar_code} alt="Bar code" boxSize="80px" objectFit="contain" mb={2} />
                        ) : (
                          <Text fontSize="sm" color="gray.500" mb={2}>No bar code</Text>
                        )}
                        {/* <Input type="file" accept="image/*" onChange={handleBarCodeChange} /> */}
                      </FormControl>

                    </Box>

                  </Flex>
                </VStack>
                <Flex gap={3} direction={{ base: "column", md: "row" }}  >
                  <Box flex="1">
                    <FileUploadField
                      // label="Clinic Icon"
                      id="clinic-edit-icon"
                      name="clinic_icon"
                      onChange={handleLogoChange}
                      selectedFile={form.clinic_icon_file}
                      helperText="Square logo recommended"
                    />
                  </Box>
                  <Box flex="1">
                    <FileUploadField
                      // label="QR Code"
                      id="clinic-edit-qr"
                      name="qr_code"
                      onChange={handleQrCodeChange}
                      selectedFile={form.qr_code_file}
                      helperText="High contrast QR code"
                    />
                  </Box>
                  <Box flex="1">
                    <FileUploadField
                      // label="Bar Code"
                      id="clinic-edit-bar"
                      name="bar_code"
                      onChange={handleBarCodeChange}
                      selectedFile={form.bar_code_file}
                      helperText="Clear barcode image recommended"
                    />
                  </Box>
                </Flex>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting || isFetching}
              isDisabled={!clinicId || isFetching}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ClinicEditModal;
